import { useQueryClient } from '@tanstack/react-query';
import { type EventContentObjectDetail, createEventContentObject } from 'models';
import { useMutation } from 'query';
import { getDateType } from 'ui';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependency
import { useContentObjectCache } from 'features/feed/queries/cache';
import type { EventContentObjectDataObject } from 'models/service';
import type { DefaultEventData } from '../../components';
import { updateEvent } from '../../services';
import { getEventDetailQueryKey, getEventsQueryKey } from '../keys';

const prepareEventsFiles = (eventsFiles: Exclude<DefaultEventData['formValues']['eventsFiles'], undefined>) =>
  eventsFiles.fileList.map((file) => file.response);

export const useUpdateEvent = () => {
  const queryClient = useQueryClient();
  const cache = useContentObjectCache();

  return useMutation<
    {
      // TODO need to return DataObject because redux for now
      eventContentDataObject: EventContentObjectDataObject;
      eventContentObject: EventContentObjectDetail;
    },
    {
      data: DefaultEventData;
      eventContentObject: EventContentObjectDetail;
    }
  >({
    async mutationFn({ data, eventContentObject }) {
      const { eventType, formValues } = data;

      const result = await updateEvent(eventContentObject.event.id, {
        /* eslint-disable @typescript-eslint/naming-convention -- BE Api interface */
        community_id: formValues.community?.value,
        cover_photo: formValues.coverPhoto?.[0]?.response,
        duplicate_invitees_from: formValues.duplicateInviteesFromEvent,
        email_invitees: undefined,
        end_time: getDateType(formValues.endTime),
        event_type: eventType,
        events_files: formValues.eventsFiles ? prepareEventsFiles(formValues.eventsFiles) : undefined,
        featured_in_channels: formValues.featuredInChannels,
        html_content: formValues.htmlContent ?? '',
        invitees: undefined,
        name: formValues.name,
        program_id: formValues.programId,
        program_topic_id: formValues.programTopicId,
        publish_at: getDateType(formValues.publishAt),
        show_in_channels: formValues.showInChannels,
        start_time: getDateType(formValues.startTime),
        tag_ids: formValues.tags,
        text_content: formValues.textContent ?? '',
        view_permission: formValues.viewPermission,
        /* eslint-enable @typescript-eslint/naming-convention -- BE Api interface */
      });

      return {
        eventContentDataObject: result.data,
        eventContentObject: createEventContentObject(result.data),
      };
    },
    async onSettled(_, __, variables) {
      await queryClient.invalidateQueries({
        queryKey: getEventDetailQueryKey({ id: variables.eventContentObject.event.id }),
      });
      await queryClient.invalidateQueries({ queryKey: getEventsQueryKey({}) });
    },
    onSuccess(data) {
      cache.replace(data.eventContentObject);
    },
  });
};
